import { useState, useEffect } from 'react';
import { ktmToday } from '../../utils/calendar';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import BrandLogo from '../../components/common/BrandLogo';
import { MdError, MdArrowForward } from 'react-icons/md';
import toast from 'react-hot-toast';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import api from '../../services/api';
import useForceDarkTheme from '../../hooks/useForceDarkTheme';
import DateField from '../../components/common/DateField';

const PHONE_RE = /^(\+\d{7,14}|\d{7,15})$/;
const normalizePhone = (ph) => ph.trim().replace(/[\s-]/g, '');

// Password strength hints — mirrors the backend rule (≥8, at least one letter AND one number).
function PasswordHints({ password, confirm }) {
  const checks = [
    { ok: password.length >= 8,      label: '8+ characters' },
    { ok: /[a-zA-Z]/.test(password), label: 'A letter' },
    { ok: /\d/.test(password),       label: 'A number' },
    { ok: password.length > 0 && password === confirm, label: 'Passwords match' },
  ];
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
      {checks.map(c => (
        <span key={c.label} className={`text-xs ${c.ok ? 'text-green-600' : 'text-gray-400'}`}>
          {c.ok ? '✓' : '○'} {c.label}
        </span>
      ))}
    </div>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="min-h-screen bg-[#141312] flex flex-col">
      <header className="bg-[#1e1d1b] border-b border-[rgba(237,232,224,0.08)] px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <BrandLogo size={24} />
          <span className="font-bold text-[#ede8e0]">TrainAxis</span>
        </div>
        <Link to="/login" className="text-sm text-[#8a847c] hover:text-[#ede8e0]">Sign in</Link>
      </header>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

export default function ClubInvite() {
  useForceDarkTheme();
  const { token }  = useParams();
  const navigate   = useNavigate();
  const { login }  = useAuth();
  const { setClubDirectly } = useTenant();

  const [state, setState] = useState('loading'); // loading | invalid | valid
  const [errorMsg, setErrorMsg] = useState('');
  const [invite, setInvite] = useState(null);

  // Form fields
  const [ownerName,    setOwnerName]    = useState('');
  const [phone,        setPhone]        = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [dateOfBirth,  setDateOfBirth]  = useState('');
  const [address,      setAddress]      = useState('');
  const [password,     setPassword]     = useState('');
  const [confirm,      setConfirm]      = useState('');
  const [showPass,     setShowPass]     = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [formError,    setFormError]    = useState('');

  // Validate the token on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/club-invitations/${token}/validate`);
        if (cancelled) return;
        if (data.valid) {
          setInvite(data);
          setState('valid');
        } else {
          setErrorMsg(data.message || 'This invitation link is not valid.');
          setState('invalid');
        }
      } catch {
        if (!cancelled) {
          setErrorMsg('We could not check this invitation. Please try again later.');
          setState('invalid');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const phoneNorm = normalizePhone(phone);
  const phoneErr = phoneTouched
    ? !phone.trim()               ? 'Phone number is required.'
    : !PHONE_RE.test(phoneNorm)   ? 'Enter a valid phone number.'
    : ''
    : '';

  const passwordValid = password.length >= 8 && /[a-zA-Z]/.test(password) && /\d/.test(password);
  const canSubmit = !submitting
    && ownerName.trim()
    && PHONE_RE.test(phoneNorm)
    && address.trim()
    && passwordValid
    && password === confirm;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setFormError('');
    setSubmitting(true);
    try {
      const { data } = await api.post(`/api/club-invitations/${token}/accept`, {
        ownerName: ownerName.trim(),
        ownerPhone: phoneNorm,
        password,
        dateOfBirth: dateOfBirth || undefined,
        address: address.trim(),
      });

      // Auto-login (registerClub-style response: { token, user, club }).
      setClubDirectly(
        { id: data.club._id, name: data.club.name, slug: data.club.slug, clubType: data.club.clubType, primaryColor: data.club.primaryColor },
        data.club.slug,
      );
      login(
        { id: data.user.id, name: data.user.name, email: data.user.email, role: data.user.role, clubId: data.user.clubId, clubSlug: data.club.slug },
        data.token,
      );
      toast.success('Welcome to TrainAxis! Your 14-day trial has started.');
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const code = err.response?.data?.code;
      const msg  = err.response?.data?.message;
      if (code === 'SLUG_TAKEN' || code === 'EMAIL_TAKEN' || code === 'EXPIRED' || code === 'REVOKED' || code === 'ACCEPTED' || code === 'INVALID') {
        // The invitation is no longer usable — send them to the error state.
        setErrorMsg(msg || 'This invitation can no longer be used.');
        setState('invalid');
      } else {
        setFormError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──
  if (state === 'loading') {
    return (
      <CenteredCard>
        <div className="bg-[#1e1d1b] rounded-2xl shadow-sm border border-[rgba(237,232,224,0.08)] p-8 text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#8a847c]">Checking your invitation…</p>
        </div>
      </CenteredCard>
    );
  }

  // ── Invalid / expired / revoked ──
  if (state === 'invalid') {
    return (
      <CenteredCard>
        <div className="bg-[#1e1d1b] rounded-2xl shadow-sm border border-[rgba(237,232,224,0.08)] p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-400/10 flex items-center justify-center mx-auto mb-4">
            <MdError className="text-2xl text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-[#ede8e0] mb-1.5">Invitation unavailable</h2>
          <p className="text-sm text-[#8a847c] mb-5">{errorMsg}</p>
          <p className="text-xs text-[#8a847c]">
            Please contact the person who invited you to request a new invitation link.
          </p>
        </div>
      </CenteredCard>
    );
  }

  // ── Valid → acceptance form (premium two-panel: brand left, form right) ──
  const urlChip = (
    <span className="inline-flex items-center rounded-full bg-nav-hover border border-divider px-3 py-1 text-xs font-medium text-secondary">
      {invite.slug}.trainaxis.com
    </span>
  );

  return (
    <div className="min-h-screen bg-[#141312] flex flex-col">
      {/* Top bar */}
      <header className="bg-[#1e1d1b] border-b border-[rgba(237,232,224,0.08)] px-6 py-4 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-2">
          <BrandLogo size={24} />
          <span className="font-bold text-[#ede8e0]">TrainAxis</span>
        </div>
        <Link to="/login" className="text-sm text-[#8a847c] hover:text-[#ede8e0]">Sign in</Link>
      </header>

      <div className="flex-1 lg:grid lg:grid-cols-2">
        {/* Brand panel (desktop) */}
        <div className="hidden lg:flex flex-col justify-center px-14 py-16 bg-[#1a1918] border-r border-[rgba(237,232,224,0.08)]">
          <div className="max-w-md">
            <div className="flex items-center gap-2 text-primary text-sm font-semibold tracking-wide mb-5">
              <FaCheckCircle /> YOU'RE INVITED
            </div>
            <h1 className="text-[2.75rem] leading-[1.05] font-bold text-[#ede8e0] tracking-tight mb-5">{invite.clubName}</h1>
            {urlChip}
            <p className="text-[#8a847c] text-base leading-relaxed mt-7">
              Welcome aboard. Set up your account and you'll be ready to manage members, classes, attendance and fees — all from one place.
            </p>
          </div>
          <p className="text-xs text-[#6b6560] mt-16">Powered by TrainAxis</p>
        </div>

        {/* Form panel */}
        <div className="flex items-start lg:items-center justify-center p-6 sm:p-8 lg:p-12 overflow-y-auto">
          <div className="w-full max-w-md">
            {/* Mobile brand header */}
            <div className="lg:hidden mb-6">
              <div className="flex items-center gap-2 text-primary text-sm font-semibold mb-2">
                <FaCheckCircle /> You're invited
              </div>
              <h2 className="text-2xl font-bold text-[#ede8e0] tracking-tight mb-3">{invite.clubName}</h2>
              {urlChip}
            </div>

            <div className="bg-[#1e1d1b] rounded-2xl shadow-sm border border-[rgba(237,232,224,0.08)] p-6 sm:p-8">
              <h3 className="hidden lg:block text-lg font-bold text-[#ede8e0] mb-1">Create your account</h3>
              <p className="hidden lg:block text-sm text-[#8a847c] mb-6">Set your password and you're ready to run your club.</p>

              <div className="space-y-4">
          {/* Email (locked to the invite) */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Email</label>
            <input value={invite.email} readOnly
              className="w-full border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm bg-[#1a1918] text-[#8a847c] cursor-default" />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Your Name</label>
            <input value={ownerName} onChange={e => setOwnerName(e.target.value)}
              placeholder="Full name"
              className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-primary transition-colors" />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Phone Number</label>
            <input
              value={phone}
              onChange={e => {
                const raw = e.target.value;
                let filtered = raw.replace(/[^\d+\s-]/g, '');
                const hasPlus = filtered.startsWith('+');
                filtered = filtered.replace(/\+/g, '');
                if (hasPlus) filtered = '+' + filtered;
                setPhone(filtered);
              }}
              onBlur={() => setPhoneTouched(true)}
              type="tel" inputMode="numeric" autoComplete="tel"
              placeholder="9841234567 or +9779841234567"
              className={`w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none transition-colors ${
                phoneErr ? 'border-red-400 focus:border-red-400' : 'focus:border-primary'
              }`} />
            {phoneErr && (
              <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                <MdError className="text-sm flex-shrink-0" /> {phoneErr}
              </p>
            )}
          </div>

          {/* Date of birth (optional) — compact, tap-to-open (closed by default) */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">
              Date of Birth <span className="text-xs text-[#8a847c] font-normal">(optional)</span>
            </label>
            <DateField value={dateOfBirth} onChange={setDateOfBirth} label="Date of birth" placeholder="Select date (optional)" max={ktmToday()} />
          </div>

          {/* Address (required) */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Address <span className="text-red-400">*</span></label>
            <input
              value={address}
              onChange={e => setAddress(e.target.value)}
              type="text" autoComplete="street-address"
              placeholder="Street / tole, city, district"
              className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-primary transition-colors" />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Password</label>
            <div className="relative">
              <input value={password} onChange={e => setPassword(e.target.value)}
                type={showPass ? 'text' : 'password'} placeholder="At least 8 characters"
                className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 pr-10 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-primary transition-colors" />
              <button type="button" onClick={() => setShowPass(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a847c]">
                {showPass ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Confirm */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Confirm Password</label>
            <input value={confirm} onChange={e => setConfirm(e.target.value)}
              type={showPass ? 'text' : 'password'} placeholder="Re-enter your password"
              className={`w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none transition-colors ${
                confirm && confirm !== password ? 'border-red-400 focus:border-red-400' : 'focus:border-primary'
              }`} />
            {(password || confirm) && <PasswordHints password={password} confirm={confirm} />}
          </div>

          {formError && (
            <div className="flex items-start gap-2 bg-red-400/10 text-red-400 rounded-xl p-3 text-sm">
              <MdError className="text-lg flex-shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}

          <button onClick={handleSubmit} disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-1.5 bg-primary text-white px-6 rounded-full text-sm font-semibold min-h-[48px] hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? 'Creating your club…' : 'Create my club account'} {!submitting && <MdArrowForward />}
          </button>
              </div>
            </div>
            <p className="lg:hidden text-center text-xs text-[#6b6560] mt-5">Powered by TrainAxis</p>
          </div>
        </div>
      </div>
    </div>
  );
}
